<template>
  <v-layout>
    <v-flex
      v-if="
        items.length > 0 || !forItinerary || (!noFilters && items.length == 0)
      "
      xs12
    >
      <v-row>
        <v-col cols="12" class="py-0">
          <v-container fluid py-0>
            <v-row align="center">
              <div>
                <h1 class="headline" v-if="!forItinerary">Receipts</h1>
              </div>
              <div>
                <slot name="leftHeader"></slot>
              </div>
              <v-col class="py-0">
                <list-filters
                  :filters="filters"
                  label="Find Receipt"
                  ref-name="paymentsListFilters"
                  @searchParamsChanged="searchParamsChanged($event)"
                  :cached-filters="cachedFilters"
                  :expansionPanel="false"
                ></list-filters>
              </v-col>
              <div>
                <ag-grid-switch :name="refNameColumns"></ag-grid-switch>
              </div>
              <div>
                <v-select
                  v-if="showGroupedView"
                  :items="groupByOptions"
                  v-model="selectedGroupedBy"
                  label="Group by"
                  item-text="name"
                  return-object
                  :outlined="outlinedInputs"
                  hide-details
                  dense
                  class="mx-2"
                ></v-select>
              </div>
              <v-switch
                prepend-icon="mdi-view-list"
                class="mr-3"
                label="Group View"
                v-model="showGroupedView"
              ></v-switch>
              <div>
                <list-visible-columns-and-filter
                  :visible-columns="visibleColumns"
                  :selectable-headers="selectableHeaders"
                  v-model="search"
                  @change="onVisibleColumnsChange"
                ></list-visible-columns-and-filter>
              </div>
              <div>
                <v-btn
                  v-if="
                    !$store.getters.hasAccessTo('finances:receipts_view_only')
                  "
                  dark
                  fab
                  color="primary"
                  class="ml-2"
                  @click="onNewPayment"
                >
                  <v-icon dark>mdi-plus</v-icon>
                </v-btn>
              </div>
            </v-row>
          </v-container>
        </v-col>
      </v-row>
      <v-row v-if="showGroupedView">
        <v-col class="py-0">
          <list-grouped :group="selectedGroupedBy" :items="items">
            <template v-slot:table="{ items }">
              <v-card outlined>
                <pi-payments-table
                  :items="items"
                  :headers="filteredHeaders"
                  :search="search"
                  @delete="deleteItem"
                  @edit="editItem"
                  @export-to-xero="onBatchExportToAccounts"
                ></pi-payments-table>
              </v-card>
            </template>
          </list-grouped>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col class="py-0">
          <div v-if="showAgGrid" key="ag-grid">
            <ag-grid-vue
              class="ag-theme-alpine"
              id="ag-payments-list"
              :columnDefs="agGridColumns"
              :rowData="items"
              rowSelection="multiple"
              :context="agContext"
              @grid-ready="onAgGridReady"
              @columnMoved="onAgColumnMoved"
              @columnResized="onAgColumnResized"
              @sortChanged="onAgSortChanged"
              @selection-changed="onAgSelectionChanged"
              @newColumnsLoaded="onAgNewColumns"
              suppressDragLeaveHidesColumns
            >
            </ag-grid-vue>
            <list-actions
              v-if="selected.length > 0"
              style="top: -30px; position: relative"
              :selected="selected"
              list="payments"
              :hideExport="hideExport"
              :columns="visibleColumns"
              :filter="searchParams"
            >
            </list-actions>
          </div>
          <v-data-table
            v-else
            v-bind="pagingOptions"
            :headers="filteredHeaders"
            :items="items"
            :loading="$apollo.loading"
            :search="search"
            item-key="id"
            class="elevation-1"
            v-model="selected"
            show-select
            fixed-header
            hide-default-footer
            @update:options="onListOptionsUpdate"
            :height="gridHeight"
            :sort-by="sortBy"
            :sort-desc="sortDesc"
            @current-items="dataTableReady"
          >
            <template v-slot:item.itineraryReference="{ item }">
              <reference-label
                v-model="item.itineraryReference"
                :itinerary-id="item.itinerary ? item.itinerary.id : ''"
              ></reference-label>
            </template>
            <template v-slot:item.itineraryBookedBy="{ item }">
              {{ item.itinerary ? item.itinerary.bookedByName : "" }}
            </template>
            <template v-slot:item.itineraryStartDate="{ item }">{{
              item.itineraryStartDate | formatDate
            }}</template>
            <template v-slot:item.amount="{ item }">{{
              item.amount | priceLabeler
            }}</template>
            <template v-slot:item.createdAt="{ item }">{{
              item.createdAt | formatDate
            }}</template>
            <template v-slot:item.lodgementDate="{ item }">{{
              item.lodgementDate | formatDate
            }}</template>
            <template v-slot:item.internalReference="{ item }">{{
              item.itinerary ? item.itinerary.internalReference : ""
            }}</template>
            <template v-slot:item.itineraryInternalStatus="{ item }">{{
              item.itinerary ? item.itinerary.internalStatus : ""
            }}</template>
            <template v-slot:item.itineraryStatus="{ item }">{{
              item.itinerary ? item.itinerary.bookingStatus : ""
            }}</template>
            <template v-slot:item.clientSurname="{ item }">{{
              item.itinerary ? item.itinerary.clientSurname : ""
            }}</template>
            <template v-slot:item.agentName="{ item }">
              <agent-label
                v-model="item.agentName"
                :agent-id="item.itinerary ? item.itinerary.agentId : ''"
              ></agent-label>
            </template>
            <template v-slot:item.exported="{ item }">
              <v-simple-checkbox
                v-model="item.exported"
                :disabled="true"
                class="ma-0"
              ></v-simple-checkbox>
            </template>
            <template v-slot:item.exportedAt="{ item }">{{
              item.exportedAt | formatDate
            }}</template>
            <template v-slot:item.actions="{ item }">
              <default-list-actions
                :item="item"
                :hideView="true"
                :hide-remove="hideRemove"
                @deleteItem="deleteItem($event)"
                @viewItem="viewItem($event)"
                @editItem="editPayment($event)"
              ></default-list-actions>
            </template>
            <template v-slot:footer>
              <list-actions
                v-if="selected.length > 0"
                :selected="selected"
                list="payments"
                :columns="visibleColumns"
                :filter="listExportFilters"
                :hideExport="hideExport"
                show-sage
                @update="onListUpdate"
                @close="selected = []"
              >
                <v-btn
                  v-if="showXero"
                  fab
                  dark
                  small
                  color="blue darken-1"
                  @click="onBatchExportToAccounts(selected)"
                >
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on">mdi-cloud-upload</v-icon>
                    </template>
                    Export to Accounts
                  </v-tooltip>
                </v-btn>
              </list-actions>
              <v-row justify="center" v-if="selected.length > 0">
                <h3 class="py-2 caption">
                  {{ selected.length }} record(s) selected
                </h3>
              </v-row>
            </template>
          </v-data-table>
        </v-col></v-row
      >
      <get-more-results
        :first="first"
        @limitChange="onLimitChange"
        :items="payments"
        :totalItems="totalItems"
        :loading="$apollo.loading"
        :loadMoreEnabled="loadMoreEnabled"
        @loadMore="loadMore('payments')"
      ></get-more-results>
      <v-flex>
        <v-row
          ><list-totals v-model="totalsColumns" :items="items"></list-totals
        ></v-row>
      </v-flex>
    </v-flex>

    <v-flex
      v-else-if="
        forItinerary && items.length == 0 && noFilters && showNewReceiptButton
      "
      xs12
    >
      <v-row justify="center" align="center" class="pt-3">
        <v-btn fab dark large color="blue darken-1" @click="onNewPayment">
          <v-icon dark>mdi-receipt</v-icon>
        </v-btn>
      </v-row>
      <v-row justify="center" class="pt-1">
        <v-btn text color="primary" @click="onNewPayment"
          >Create New Receipt</v-btn
        >
      </v-row>
      <v-row v-if="debug" sm12
        >itinerary: <code>{{ itineraryParticipants }}</code></v-row
      >
    </v-flex>

    <payment-edit-dialog
      :show="showPaymentDialog"
      @close="showPaymentDialog = false"
      @save="onPaymentSave"
      v-model="editedPayment"
      :key="editorKey"
      @export-to-xero="onExportToXero"
      @export-to-quickbooks="onExportToQuickBooks"
      :itinerary-participants="itineraryParticipants"
    ></payment-edit-dialog>
    <batch-create-with-filters
      :show="showBatchCreate"
      @close="showBatchCreate = false"
      @complete="onBatchPaymentsComplete"
    />
    <task-status
      :show="showBatchExportTaskStatus"
      v-model="batchExportTask"
      @complete="onBatchExportToAccountsComplete"
    ></task-status>
  </v-layout>
</template>
<script>
import { ListMixin } from "../../../../mixins/ListMixin";
import {
  ALL_PAYMENTS_SEARCH_QUERY,
  PAYMENT_DELETE_MUTATION,
  PAYMENT_CREATE_MUTATION,
  PAYMENT_UPDATE_MUTATION,
  EXPORT_PAYMENT_TO_XERO_MUTATION,
  EXPORT_PAYMENT_TO_QUICK_BOOKS_MUTATION,
} from "../../../../constants/graphql";
import GetMoreResults from "../../shared/GetMoreResults";
import DefaultListActions from "../../shared/DefaultListActions";
import PaymentEditDialog from "./new/PaymentEditDialog";
import ListFilters from "../../shared/list-filters/ListFilters";
import receiptsFilters from "./receipts-filters";
import ListVisibleColumnsAndFilter from "../../shared/ListVisibleColumnsAndFilter";
import {
  removeGQLTypename,
  sortByDate,
  sortByFn,
  sortByReference,
  sortByDateWithFormat,
  itiItemDuration,
} from "../../../../helpers/index";
import ReferenceLabel from "../../shared/labels/ReferenceLabel";
import AgentLabel from "../../shared/labels/AgentLabel";
import ListActions from "../../shared/list-actions/ListActions";
import AgGridListMixin from "../../../../mixins/AgGridListMixin";
import { AgGridVue } from "ag-grid-vue";
import AgGridSwitch from "../../shared/AgGridSwitch";
import ListGrouped from "../../shared/ListGrouped.vue";
import PiPaymentsTable from "../purchase-invoice-payments/PiPaymentsTable.vue";
import BatchCreateWithFilters from "./BatchCreateWithFilters.vue";
import ListTotals from "../../shared/ListTotals.vue";
import BatchExportPaymentsToAccountsMixin from "./batch-export-payments-to-accounts-mixin";
import TaskStatus from "../../shared/TaskStatus";
const randomize = require("randomatic");
export default {
  name: "PaymentList",
  mixins: [ListMixin, AgGridListMixin, BatchExportPaymentsToAccountsMixin],
  components: {
    GetMoreResults,
    DefaultListActions,
    PaymentEditDialog,
    ListFilters,
    ListVisibleColumnsAndFilter,
    ReferenceLabel,
    AgentLabel,
    ListActions,
    AgGridVue,
    AgGridSwitch,
    ListGrouped,
    PiPaymentsTable,
    BatchCreateWithFilters,
    ListTotals,
    TaskStatus,
  },
  props: {
    customFilters: {
      type: Object,
    },
    itinerary: {
      type: Object,
      default: () => null,
    },
    cachedFilters: {
      type: Boolean,
      default: () => true,
    },
    refName: {
      type: String,
      default: () => "PaymentList",
    },
    refNameColumns: {
      type: String,
      default: () => "PaymentListCols",
    },
  },

  data() {
    return {
      debug: false,
      selected: [],
      editorKey: randomize("Aa0", 32),
      visibleColumns: this.$store.getters.localSettings(this.refNameColumns, [
        "payerName",
        "payerSurname",
        "amount",
        "currencyCode",
        "actions",
      ]),
      receiptsFilters: receiptsFilters,
      headers: [
        {
          text: "Payer Name",
          value: "payerName",
          name: "payerName",
          sortable: true,
        },
        {
          text: "Payer Surname",
          value: "payerSurname",
          name: "payerSurname",
          sortable: true,
        },
        {
          text: "Amount",
          value: "amount",
          name: "amount",
          sortable: true,
          groupBy: "currencyCode",
          valueGetter: (payload) =>
            this.$options.filters.priceLabeler(payload.data.amount),
        },

        {
          text: "Currency",
          value: "currencyCode",
          name: "currencyCode",
          sortable: true,
        },
        {
          text: "Ex Rate",
          value: "exchangeRate",
          name: "exchangeRate",
          sortable: true,
        },
        {
          text: "Created At",
          value: "createdAt",
          name: "createdAt",
          sortable: true,
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.createdAt),
        },
        {
          text: "Lodgement Date",
          value: "lodgementDate",
          name: "lodgementDate",
          sortable: true,
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.lodgementDate),
        },
        {
          text: "Lodgement Ref",
          value: "lodgementReference",
          name: "lodgementReference",
          sortable: true,
        },
        {
          text: "Created By",
          value: "createdBy.name",
          name: "createdByName",
          sortable: true,
        },
        {
          text: "Exported",
          value: "exported",
          name: "exported",
          sortable: true,
        },
        {
          text: "Exported At",
          value: "exportedAt",
          name: "exportedAt",
          sortable: true,
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.exportedAt),
        },
        {
          text: "Exported By",
          value: "exportedByName",
          name: "exportedByName",
          sortable: true,
        },
        {
          text: "Department",
          value: "department",
          name: "department",
          sortable: true,
        },
        {
          text: "Iti Internal Ref",
          value: "itineraryInternalRef",
          name: "itineraryInternalRef",
          sortable: false,
        },
        {
          text: "Iti Internal Status",
          value: "itineraryInternalStatus",
          name: "itineraryInternalStatus",
          valueGetter: (payload) =>
            payload.data.itinerary ? payload.data.itinerary.internalStatus : "",
          sortable: false,
        },
        // https://tracker.globetrack.ie/issues/11373
        {
          text: "Itinerary Status",
          value: "itineraryStatus",
          name: "itineraryStatus",
          valueGetter: (payload) =>
            payload.data.itinerary ? payload.data.itinerary.bookingStatus : "",
          sortable: false,
        },
        {
          text: this.$store.getters.isCPS
            ? "Itinerary Destination"
            : "Iti Brand",
          value: "itineraryBrand",
          name: "itineraryBrand",
          sortable: false,
        },
        {
          text: "Client Surname",
          value: "clientSurname",
          name: "clientSurname",
          sortable: false,
          valueGetter: (payload) =>
            payload.data.itinerary ? payload.data.itinerary.clientSurname : "",
        },
        {
          text: "Batch No",
          value: "batchNo",
          name: "batchNo",
          sortable: true,
        },
        {
          text: "Type",
          value: "type",
          name: "type",
          sortable: true,
        },
        {
          text: "Payment Method",
          value: "paymentMethodName",
          name: "paymentMethodName",
          sortable: true,
        },
        {
          text: "Notes",
          value: "notes",
          name: "notes",
          sortable: true,
        },
        {
          text: "Iti Ref",
          value: "itineraryReference",
          name: "itineraryReference",
          sortable: true,
          sort: sortByReference,
          comparator: sortByReference,
          cellRendererFramework: "ReferenceLabel",
        },
        {
          text: "Itinerary Agent",
          value: "agentName",
          name: "agentName",
          cellRendererFramework: "AgentLabel",
          sortable: true,
        },
        {
          text: "Agent Account Code",
          value: "agentAccountCode",
          name: "agentAccountCode",
          sortable: false,
        },
        {
          text: "Iti Start Date",
          value: "itineraryStartDate",
          name: "itineraryStartDate",
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.itineraryStartDate),
          sortable: true,
        },
        {
          text: "Iti Booked by",
          value: "itineraryBookedBy",
          name: "itineraryBookedBy",
          valueGetter: (payload) =>
            payload.data.itinerary ? payload.data.itinerary.bookedByName : "",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
          cellRendererFramework: "DefaultListActions",
          valueGetter: (payload) => ({
            agGridViewOptions: {
              hideRemove:
                !this.$store.getters.hasAccessTo("finances:delete") ||
                this.$store.getters.hasAccessTo("finances:receipts_view_only"),
              hideEdit: false,
            },
          }),
        },
      ],
      editedPayment: null,
      newPayment: {
        type: "Cash",
        lodgementDate: new Date().toISOString(false),
      },
      showPaymentDialog: false,
      showGroupedView: false,
      selectedGroupedBy: {
        name: "Batch No",
        getKey: (item) => Number(item.batchNo),
        getLabel: (item) => `Batch No: ${item.batchNo}`,
        reverse: true,
      },
      groupByOptions: [
        {
          name: "Batch No",
          getKey: (item) => item.batchNo,
          getLabel: (item) => `Batch No: ${item.batchNo}`,
          sort: (item) => Number(item.batchNo),
        },
        {
          name: "Lodgement ref",
          getKey: (item) => item.lodgementReference,
          getLabel: (item) => item.lodgementReference,
        },
        {
          name: "Payer",
          getKey: (item) => `${item.payerName} ${item.payerSurname}`,
          getLabel: (item) => `${item.payerName} ${item.payerSurname}`,
        },
      ],
      showBatchCreate: false,
    };
  },

  computed: {
    itineraryParticipants() {
      return this.itinerary
        ? this.itinerary.participants.edges.map(removeGQLTypename)
        : [];
    },
    listExportFilters() {
      return {
        ...this.customFilters,
        ...this.searchParams,
      };
    },
    gridHeight() {
      if (!this.fixedHeader) {
        return null;
      }
      return this.items && this.items.length >= 20
        ? this.windowHeight - 270
        : null;
    },
    totalsColumns() {
      const cols = ["amount"];
      return this.filteredHeaders.filter((h) => cols.indexOf(h.name) != -1);
    },
    hideExport() {
      return !this.$store.getters.hasAccessTo("finances:export");
    },
    showXero() {
      return this.$store.getters.hasAccessTo("finances:xero_export");
    },

    showNewReceiptButton() {
      return (
        this.$store.getters.hasAccessTo("finances:receipts") &&
        !this.$store.getters.hasAccessTo("finances:receipts_view_only")
      );
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    hideRemove() {
      return (
        !this.$store.getters.hasAccessTo("finances:delete") ||
        this.$store.getters.hasAccessTo("finances:receipts_view_only")
      );
    },
    filters() {
      return receiptsFilters.map((f) => {
        f.props.outlined = this.$store.getters.localSettings("outlinedInputs");
        if (this.$store.getters.isCPS) {
          if (f.props.filterName == "brands_eq") {
            f.props.label = "Itinerary Destination";
          }
        }
        return f;
      });
    },
    items() {
      return this.payments && this.payments.edges
        ? this.payments.edges.map(removeGQLTypename)
        : [];
    },
    itemsForGroup() {
      return this.showGroupedView &&
        this.selectedGroupedBy &&
        this.selectedGroupedBy.sort
        ? this.items.sort(sortByFn(this.selectedGroupedBy.sort))
        : this.items;
    },
    forItinerary() {
      return this.customFilters && this.customFilters.itinerary_id != "";
    },
    noFilters() {
      return Object.keys(this.searchParams).length < 1;
    },
    showPaymentMethods() {
      return this.$store.getters.hasAccessTo("finances:payment_methods");
    },
    selectableHeaders() {
      const baseHeaders = this.headers.filter((h) => h.name != "menuActions");
      const filtered = this.showPaymentMethods
        ? baseHeaders
        : baseHeaders.filter((h) => h.name !== "paymentMethodName");
      return filtered.sort(sortByFn((h) => h.name));
    },
    filteredHeaders() {
      const headers = this.showPaymentMethods
        ? this.headers
        : this.headers.filter((h) => h.name !== "paymentMethodName");
      const hs = [];
      for (const headerName of this.visibleColumns) {
        const h = headers.find((h) => h.name == headerName);
        if (h) {
          hs.push(h);
        }
      }
      return this.hideActions ? hs.filter((h) => h.name != "actions") : hs;
    },
  },

  methods: {
    onBatchExportToAccounts(list) {
      const records = list && list.length ? list : this.selected;
      this.batchExportToAccounts(records, "payments", "xero");
    },
    onExportToXero(item) {
      const id = item && item.id ? item.id : this.editedPayment.id;
      return this.exportPaymentToXero(id);
    },
    exportPaymentToXero(id) {
      return this.$apollo
        .mutate({
          mutation: EXPORT_PAYMENT_TO_XERO_MUTATION,
          variables: {
            input: { id },
          },
        })
        .then(({ data }) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: data.exportPaymentToXero.message,
            color: data.exportPaymentToXero.status,
          });
        })
        .catch((err) => {
          console.log(err);
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
    exportSavedPayment(id, exportTo) {
      if (!id || !exportTo) return;
      if (exportTo == "xero") {
        this.exportPaymentToXero(id);
      } else if (exportTo == "quickbooks") {
        this.exportPaymentToQuickBooks(id);
      }
    },
    onBatchPaymentsComplete() {
      //this.showBatchCreate = false;
      this.onListUpdate();
    },
    onNewPayment() {
      const amount =
        !this.$store.getters.hasAccessTo("finances:receipt_null") &&
        this.itinerary &&
        this.itinerary.balance
          ? this.itinerary.balance
          : 0;
      if (this.forItinerary) {
        this.editedPayment = {
          ...this.newPayment,
          amount: amount,
          currencyCode: this.itinerary
            ? this.itinerary.currencyCode
            : undefined,
          payerName: this.itinerary
            ? this.itinerary.participants.edges[0].node.name
            : "",
          payerSurname: this.itinerary
            ? this.itinerary.participants.edges[0].node.surname
            : "",
        };
        this.editorKey = randomize("Aa0", 32);
        this.showPaymentDialog = true;
      } else {
        this.showBatchCreate = true;
        // if (
        //   confirm(
        //     "All RECEIPTS must be entered from the Itinerary List. Continue?"
        //   )
        // ) {
        //   this.$router.push("/itineraries");
        // }
      }
    },
    editItem(payload) {
      this.editPayment(payload);
    },
    editPayment(item) {
      this.editorKey = randomize("Aa0", 32);
      this.editedPayment = { ...item };
      this.showPaymentDialog = true;
    },
    onPaymentSave(payload, exportTo = null) {
      this.showPaymentDialog = false;
      if (payload.id) {
        this.updatePayment(payload, exportTo);
      } else {
        if (this.itinerary) {
          this.$apollo
            .mutate({
              mutation: PAYMENT_CREATE_MUTATION,
              variables: {
                ...payload,
                itineraryId: this.itinerary.id,
              },
              refetchQueries: [
                {
                  query: ALL_PAYMENTS_SEARCH_QUERY,
                  variables: {
                    first: this.first,
                    after: this.after,
                    filter: {
                      ...this.customFilters,
                      ...this.searchParams,
                    },
                    sort: this.sort,
                  },
                },
              ],
            })
            .then(({ data }) => {
              this.$store.dispatch("showAppSnackbar", {
                msg: "Payment Created.",
              });
              this.exportSavedPayment(data.createPayment.id, exportTo);
            })
            .catch((err) => console.error(err));
        } else {
          console.error("Cannot create payment without itinerary!");
        }
      }
    },
    updatePayment(payload, exportTo = null) {
      this.$apollo
        .mutate({
          mutation: PAYMENT_UPDATE_MUTATION,
          variables: {
            ...payload,
          },
          refetchQueries: [
            {
              query: ALL_PAYMENTS_SEARCH_QUERY,
              variables: {
                after: this.after,
                filter: {
                  ...this.customFilters,
                  ...this.searchParams,
                },
                sort: this.sort,
              },
            },
          ],
        })
        .then((res) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Payment Updated.",
          });
          this.exportSavedPayment(payload.id, exportTo);
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
    deleteItem(item) {
      this.$apollo
        .mutate({
          mutation: PAYMENT_DELETE_MUTATION,
          variables: {
            id: item.id,
          },
          refetchQueries: [
            {
              query: ALL_PAYMENTS_SEARCH_QUERY,
              variables: {
                after: this.after,
                filter: {
                  ...this.customFilters,
                  ...this.searchParams,
                },
                sort: this.sort,
              },
            },
          ],
        })
        .then((res) =>
          this.$store.dispatch("showAppSnackbar", { msg: "Payment removed." })
        )
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },

    onListUpdate() {
      if (this.$apollo.queries.payments != null) {
        this.$apollo.queries.payments.refetch();
        //this.selected = [];
      }
    },
    onExportToQuickBooks(item) {
      return this.exportPaymentToQuickBooks(item.id);
    },
    exportPaymentToQuickBooks(id) {
      return this.$apollo
        .mutate({
          mutation: EXPORT_PAYMENT_TO_QUICK_BOOKS_MUTATION,
          variables: { id },
        })
        .then(({ data }) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: data.exportPaymentToQuickBooks.message,
            color: data.exportPaymentToQuickBooks.success ? "success" : "error",
          });
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
  },

  created() {
    this.expandPanel = this.context == "defaultContext" ? 0 : 1;
  },

  apollo: {
    payments: {
      query: ALL_PAYMENTS_SEARCH_QUERY,
      variables() {
        return {
          after: this.after,
          first: this.first,
          filter: {
            ...this.customFilters,
            ...this.searchParams,
          },
          sort: this.sort,
        };
      },
      fetchPolicy: "network-only",
      result({ data }) {
        this.totalItems = data.payments.totalCount;
      },
    },
  },
};
</script>
<style scoped>
#ag-payments-list {
  height: calc(100vh - 320px);
}
</style>



// WEBPACK FOOTER //
// src/components/gt/finances/payments/PaymentList.vue